import {
  AddAgencyOfficerMutation,
  AdminGetAgencyQuery,
  AgencyOfficerRow,
  ConfirmAgencySampleReportUploadMutation,
  GetAgencyOfficerStatsQuery,
  GetAgencyTemplateByDomainQuery,
  InviteAllAgencyLibrariesMutation,
  InviteAgencyLibraryMutation,
  RequestAgencySampleReportUploadMutation,
  UpdateAgencyDetailsMutation,
  UpdateAgencyOfficerMutation,
  VocabularyInput,
} from '@clipr/lib';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiService } from '../../api/apiService';
import { ApiError } from '../../api/apiError';
import {
  AgencyDetailsForm,
  createAgencyDetailsForm,
  parseJson,
  toAgencyDetailsInput,
} from '../../components/agencyDetails';

export type AgencyDashboardTemplate = NonNullable<GetAgencyTemplateByDomainQuery['getAgencyTemplateByDomain']>;
export type AgencyDashboardOfficerRow = AgencyOfficerRow & {
  section: 'min' | 'max' | 'extras';
};
export type PendingOfficerRow = AgencyOfficerRow & {
  localId: string;
  error?: string;
  fieldErrors: Partial<Record<keyof AgencyOfficerRow, string>>;
};

type AgencyDashboardStore = {
  user?: {
    email: string;
    isAgencySponsor?: boolean;
  } | null;
  apiService: Pick<ApiService,
    'getAgencyTemplateByDomain' |
    'getAgencyOfficerStats' |
    'requestAgencySampleReportUpload' |
    'confirmAgencySampleReportUpload' |
    'inviteAgencyLibrary' |
    'inviteAllAgencyLibraries' |
    'updateAgencyOfficer' |
    'addAgencyOfficer' |
    'updateAgencyDetails' |
    'adminGetAgency' |
    'adminUpdateAgencyVocabulary'>;
};

type AgencyVocabulary = AdminGetAgencyQuery['adminGetAgency']['vocabulary'];

type AgencyOfficerStat = GetAgencyOfficerStatsQuery['getAgencyOfficerStats'][number];
type AgencyUploadTicket = RequestAgencySampleReportUploadMutation['requestAgencySampleReportUpload'];
type AgencyInviteAllResult = InviteAllAgencyLibrariesMutation['inviteAllAgencyLibraries'];
type AgencyInviteTeam = InviteAgencyLibraryMutation['inviteAgencyLibrary'];
type AgencyConfirmTemplate = ConfirmAgencySampleReportUploadMutation['confirmAgencySampleReportUpload'];
type AgencyUpdateOfficerTemplate = UpdateAgencyOfficerMutation['updateAgencyOfficer'];
type AgencyAddOfficerTemplate = AddAgencyOfficerMutation['addAgencyOfficer'];
type AgencyUpdateDetailsTemplate = UpdateAgencyDetailsMutation['updateAgencyDetails'];

export class AgencyDashboardPageState {

  constructor(readonly store: AgencyDashboardStore) {
    makeObservable(this);
  }

  @observable template: AgencyDashboardTemplate | null = null;
  @observable officerStats: AgencyOfficerStat[] = [];
  @observable isLoading = false;
  @observable isUploadingSample = false;
  @observable isInvitingAll = false;
  @observable inviteInProgressEmail: string | null = null;
  @observable editInProgressEmail: string | null = null;
  @observable error: string | null = null;
  @observable sampleUploadError: string | null = null;
  @observable lastUploadTicket: AgencyUploadTicket | null = null;
  @observable inviteAllStatus: string | null = null;
  @observable.shallow rowErrors = new Map<string, string>();
  @observable.shallow pendingExtras: PendingOfficerRow[] = [];

  @observable.shallow savedVocabulary: AgencyVocabulary = [];
  @observable isDictionaryOpen = false;
  @observable isSavingVocabulary = false;
  @observable vocabularyError: string | null = null;
  @observable.shallow vocabulary: VocabularyInput[] = [];

  @observable isDetailsOpen = false;
  @observable isSavingDetails = false;
  @observable detailsError: string | null = null;
  @observable.shallow detailsForm: AgencyDetailsForm = createAgencyDetailsForm(null);
  private detailsSaveSeq = 0;

  @computed
  get emailDomain(): string | null {
    return this.store.user?.email.split('@')[1]?.toLowerCase() ?? null;
  }

  @computed
  get officerRows(): AgencyDashboardOfficerRow[] {
    return [
      ...parseRows(this.template?.minOfficers).map((row) => ({ ...row, section: 'min' as const })),
      ...parseRows(this.template?.maxOfficers).map((row) => ({ ...row, section: 'max' as const })),
      ...this.extrasRows,
    ];
  }

  @computed
  get extrasRows(): AgencyDashboardOfficerRow[] {
    return parseRows(this.template?.extras).map((row) => ({ ...row, section: 'extras' as const }));
  }

  @computed
  get officerEmailToTeamId(): Record<string, string> {
    return normalizeEmailMap(this.template?.officerEmailToTeamId);
  }

  @computed
  get invitedCount(): number {
    return Object.keys(this.officerEmailToTeamId).length;
  }

  @computed
  get officerStatsByTeamId(): Record<string, AgencyOfficerStat> {
    return Object.fromEntries(this.officerStats.map((s) => [s.teamId, s]));
  }

  @computed
  get isInviteReady(): boolean {
    return Boolean(this.template?.templateTeamId && this.template?.sourceCustomSpecialtyId);
  }

  @computed
  get agencyName(): string {
    return this.template?.agencyName ?? 'Agency';
  }

  @computed
  get sampleStatus(): string {
    if (!this.template)
      return 'Not loaded';

    if (this.template.samplePoliceReportS3Key && this.template.sourceCustomSpecialtyId)
      return 'Sample trained';

    if (this.template.samplePoliceReportS3Key)
      return 'Sample uploaded';

    return 'Sample report needed';
  }

  @action
  async mounted() {
    if (!this.store.user?.isAgencySponsor)
      return;

    await this.loadTemplate();
  }

  @action
  unmounted() {
    this.error = null;
    this.sampleUploadError = null;
    // This state object is a store singleton, so a modal left open survives navigation and
    // reopens over stale form values on the way back.
    this.isDetailsOpen = false;
    this.isSavingDetails = false;
    this.detailsError = null;
    // Coming back to the same agency keeps orgId identical, so the org check alone would let a
    // save started before the unmount land on the fresh session. Retire the token instead.
    this.detailsSaveSeq++;
  }

  @action
  async loadTemplate() {
    if (!this.emailDomain) {
      this.error = 'Admin email domain is missing.';
      return;
    }

    this.isLoading = true;
    this.error = null;

    const [res, err] = await this.store.apiService.getAgencyTemplateByDomain({ domain: this.emailDomain });

    runInAction(() => {
      this.isLoading = false;
      if (err) {
        this.error = err.message;
        return;
      }

      this.template = res?.getAgencyTemplateByDomain ?? null;
      if (!this.template)
        this.error = `No agency template found for ${this.emailDomain}.`;
    });

    if (this.template) {
      void this.loadOfficerStats();
      void this.loadVocabulary();
    }
  }

  @action
  async loadOfficerStats() {
    if (!this.template)
      return;

    const [res] = await this.store.apiService.getAgencyOfficerStats({ orgId: this.template.orgId });

    runInAction(() => {
      this.officerStats = res?.getAgencyOfficerStats ?? [];
    });
  }

  @action
  async loadVocabulary() {
    if (!this.template)
      return;

    // adminGetAgency is authorized for the owning sponsor (assertCanManageAgency);
    // we only read the vocabulary slice here.
    const [res] = await this.store.apiService.adminGetAgency({ orgId: this.template.orgId });

    runInAction(() => {
      this.savedVocabulary = res?.adminGetAgency.vocabulary ?? [];
    });
  }

  @action
  openDictionary() {
    this.vocabulary = this.savedVocabulary.map(v => ({
      phrase: v.phrase,
      displayAs: v.displayAs ?? undefined,
    }));
    this.vocabularyError = null;
    this.isDictionaryOpen = true;
  }

  @action
  closeDictionary() {
    if (this.isSavingVocabulary) return;
    this.isDictionaryOpen = false;
    this.vocabularyError = null;
  }

  @action
  openDetails() {
    this.detailsForm = createAgencyDetailsForm(this.template);
    this.detailsError = null;
    this.isDetailsOpen = true;
  }

  @action
  closeDetails() {
    if (this.isSavingDetails) return;
    this.isDetailsOpen = false;
    this.detailsError = null;
  }

  @action
  updateDetails(patch: Partial<AgencyDetailsForm>) {
    this.detailsForm = { ...this.detailsForm, ...patch };
  }

  @action
  async submitDetails() {
    const orgId = this.template?.orgId;
    if (!orgId) return;

    const [details, validationError] = toAgencyDetailsInput(this.detailsForm);
    if (!details) {
      this.detailsError = validationError;
      return;
    }

    // This state is a store singleton, so an in-flight save survives leaving the page. Without a
    // token its completion would reopen/close a modal and patch a template the user has moved on
    // from — or belong to an agency they are no longer looking at.
    const seq = ++this.detailsSaveSeq;
    this.isSavingDetails = true;
    this.detailsError = null;

    const [res, err] = await this.store.apiService.updateAgencyDetails({ orgId, details });

    runInAction(() => {
      if (seq !== this.detailsSaveSeq || orgId !== this.template?.orgId)
        return;
      this.isSavingDetails = false;
      if (err) {
        this.detailsError = err.message;
        return;
      }
      // Nothing to merge means the grid would keep rendering pre-save values behind a closed
      // modal, which reads as a silent success. Keep it open and say so.
      if (!res?.updateAgencyDetails) {
        this.detailsError = 'Agency details did not come back from the server. Reload and try again.';
        return;
      }
      this.applyUpdatedDetailsTemplate(res.updateAgencyDetails);
      this.isDetailsOpen = false;
    });
  }

  @action
  addVocabularyRow() {
    this.vocabulary = [...this.vocabulary, { phrase: '', displayAs: '' }];
  }

  @action
  updateVocabularyRow(index: number, patch: Partial<VocabularyInput>) {
    this.vocabulary = this.vocabulary.map((row, i) => (i === index ? { ...row, ...patch } : row));
  }

  @action
  removeVocabularyRow(index: number) {
    this.vocabulary = this.vocabulary.filter((_, i) => i !== index);
  }

  @action
  async submitVocabulary() {
    if (!this.template) return;

    const vocabulary = this.vocabulary
      .map(row => ({
        phrase: row.phrase.trim(),
        displayAs: row.displayAs?.trim() || undefined,
      }))
      .filter(row => row.phrase);

    const phrases = vocabulary.map(v => v.phrase.toLowerCase());
    if (phrases.length !== new Set(phrases).size) {
      this.vocabularyError = 'Duplicate phrases are not allowed';
      return;
    }

    this.isSavingVocabulary = true;
    this.vocabularyError = null;

    const [res, err] = await this.store.apiService.adminUpdateAgencyVocabulary({
      orgId: this.template.orgId,
      vocabulary,
    });

    runInAction(() => {
      this.isSavingVocabulary = false;
      if (err) {
        this.vocabularyError = err.message;
        return;
      }
      this.savedVocabulary = res?.adminUpdateAgencyVocabulary.vocabulary ?? [];
      this.isDictionaryOpen = false;
    });
  }

  @action
  async uploadSampleReport(file: File) {
    if (!this.template)
      return;

    this.isUploadingSample = true;
    this.sampleUploadError = null;

    const [ticketRes, ticketErr] = await this.store.apiService.requestAgencySampleReportUpload({
      orgId: this.template.orgId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    });

    if (ticketErr) {
      runInAction(() => {
        this.isUploadingSample = false;
        this.sampleUploadError = ticketErr.message;
      });
      return;
    }

    const ticket = ticketRes?.requestAgencySampleReportUpload;
    if (!ticket) {
      runInAction(() => {
        this.isUploadingSample = false;
        this.sampleUploadError = 'Upload ticket was not returned.';
      });
      return;
    }

    runInAction(() => {
      this.lastUploadTicket = ticket;
    });

    const uploadError = await postSampleFile(ticket, file).catch((error: unknown) =>
      error instanceof Error ? error.message : 'Upload failed.');
    if (uploadError) {
      runInAction(() => {
        this.isUploadingSample = false;
        this.sampleUploadError = uploadError;
      });
      return;
    }

    await this.confirmSampleUpload(ticket.ticketId);
  }

  @action
  async confirmSampleUpload(ticketId?: string) {
    if (!this.template)
      return;

    const resolvedTicketId = ticketId ?? this.lastUploadTicket?.ticketId;
    if (!resolvedTicketId)
      return;

    this.isUploadingSample = true;
    this.sampleUploadError = null;

    const [res, err] = await this.store.apiService.confirmAgencySampleReportUpload({
      orgId: this.template.orgId,
      ticketId: resolvedTicketId,
    });

    runInAction(() => {
      this.isUploadingSample = false;
      if (err) {
        this.sampleUploadError = isUploadNotFoundError(err) ?
          'Upload not found. Confirm again after the upload completes.' :
          err.message;
        return;
      }

      this.applyConfirmedTemplate(res?.confirmAgencySampleReportUpload);
    });
  }

  @action
  async editOfficer(row: AgencyDashboardOfficerRow, updates: Partial<AgencyOfficerRow>) {
    if (!this.template)
      return;

    const currentEmail = row.email;
    const currentEmailKey = normalizeEmail(currentEmail);

    const next: AgencyOfficerRow = {
      firstName: row.firstName,
      lastName: row.lastName,
      title: row.title,
      email: row.email,
      supervisorTitle: row.supervisorTitle,
      supervisorName: row.supervisorName,
      supervisorEmail: row.supervisorEmail,
      ...updates,
    };

    const newEmailKey = normalizeEmail(next.email);
    if (!newEmailKey)
      return;

    this.rowErrors.delete(currentEmailKey);
    this.rowErrors.delete(newEmailKey);
    this.editInProgressEmail = currentEmailKey;

    const [res, err] = await this.store.apiService.updateAgencyOfficer({
      orgId: this.template.orgId,
      currentEmail,
      officer: next,
    });

    runInAction(() => {
      this.editInProgressEmail = null;
      if (err) {
        this.rowErrors.set(currentEmailKey, err.message);
        return;
      }

      this.applyUpdatedRosterTemplate(res?.updateAgencyOfficer);
    });
  }

  @action
  addPendingOfficer() {
    this.pendingExtras.push({
      localId: createLocalId(),
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      supervisorTitle: '',
      supervisorName: '',
      supervisorEmail: '',
      fieldErrors: {},
    });
  }

  @action
  editPendingOfficer(localId: string, updates: Partial<AgencyOfficerRow>) {
    const index = this.pendingExtras.findIndex((r) => r.localId === localId);
    if (index === -1)
      return;

    const next = {
      ...this.pendingExtras[index],
      ...updates,
      error: undefined,
    };
    next.fieldErrors = this.validateRow(next);
    this.pendingExtras[index] = next;
  }

  @action
  removePendingOfficer(localId: string) {
    this.pendingExtras = this.pendingExtras.filter((r) => r.localId !== localId);
  }

  @action
  async commitPendingOfficer(localId: string) {
    if (!this.template)
      return;

    const row = this.pendingExtras.find((r) => r.localId === localId);
    if (!row)
      return;

    const fieldErrors = this.validateRow(row);
    if (Object.keys(fieldErrors).length > 0 || !isOfficerComplete(row)) {
      const index = this.pendingExtras.findIndex((r) => r.localId === localId);
      if (index !== -1)
        this.pendingExtras[index] = { ...row, fieldErrors };
      return;
    }

    const officer: AgencyOfficerRow = {
      firstName: row.firstName.trim(),
      lastName: row.lastName.trim(),
      title: row.title.trim(),
      email: row.email.trim().toLowerCase(),
      supervisorTitle: row.supervisorTitle.trim(),
      supervisorName: row.supervisorName.trim(),
      supervisorEmail: row.supervisorEmail.trim().toLowerCase(),
    };

    const [res, requestErr] = await this.store.apiService.addAgencyOfficer({
      orgId: this.template.orgId,
      officer,
    });

    runInAction(() => {
      if (requestErr) {
        const index = this.pendingExtras.findIndex((r) => r.localId === localId);
        if (index !== -1)
          this.pendingExtras[index] = { ...this.pendingExtras[index], error: requestErr.message };
        return;
      }

      this.applyAddedTemplate(res?.addAgencyOfficer);
      this.pendingExtras = this.pendingExtras.filter((r) => r.localId !== localId);
    });
  }

  validateRow(row: AgencyOfficerRow): Partial<Record<keyof AgencyOfficerRow, string>> {
    const errors: Partial<Record<keyof AgencyOfficerRow, string>> = {};
    const domain = this.template?.domain ?? '';

    const checkEmail = (key: 'email' | 'supervisorEmail', value: string) => {
      if (!value)
        return;

      if (!/^\S+@\S+\.\S+$/.test(value)) {
        errors[key] = 'Not a valid email';
        return;
      }

      const valueDomain = value.split('@')[1]?.toLowerCase() ?? '';
      if (domain && valueDomain !== domain.toLowerCase())
        errors[key] = `Email must end with @${domain}`;
    };
    checkEmail('email', row.email);
    checkEmail('supervisorEmail', row.supervisorEmail);

    const emailKey = row.email.trim().toLowerCase();
    if (emailKey) {
      const existing = this.officerRows.find((r) => r.email.trim().toLowerCase() === emailKey);
      const otherPending = this.pendingExtras.find((r) =>
        'localId' in r && r.localId !== (row as PendingOfficerRow).localId &&
        r.email.trim().toLowerCase() === emailKey);
      if (existing || otherPending)
        errors.email = 'Already used by another officer';
    }

    return errors;
  }

  @action
  async inviteOfficer(row: AgencyDashboardOfficerRow) {
    if (!this.template)
      return;

    const emailKey = normalizeEmail(row.email);
    this.inviteInProgressEmail = emailKey;
    this.rowErrors.delete(emailKey);

    const [res, err] = await this.store.apiService.inviteAgencyLibrary({
      orgId: this.template.orgId,
      officer: toOfficerInput(row),
    });

    runInAction(() => {
      this.inviteInProgressEmail = null;
      if (err) {
        this.rowErrors.set(emailKey, err.message);
        return;
      }

      this.markOfficerInvited(row.email, res?.inviteAgencyLibrary);
    });

    if (!err)
      await this.loadOfficerStats();
  }

  @action
  async inviteAll() {
    if (!this.template)
      return;

    this.isInvitingAll = true;
    this.inviteAllStatus = null;
    this.rowErrors.clear();

    const [res, err] = await this.store.apiService.inviteAllAgencyLibraries({ orgId: this.template.orgId });

    runInAction(() => {
      this.isInvitingAll = false;
      if (err) {
        this.error = err.message;
        return;
      }

      this.applyInviteAllResult(res?.inviteAllAgencyLibraries);
    });

    if (!err)
      void this.loadOfficerStats();
  }

  @action
  private applyConfirmedTemplate(template?: AgencyConfirmTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      sourceCustomSpecialtyId: template.sourceCustomSpecialtyId,
      samplePoliceReportS3Key: template.samplePoliceReportS3Key,
      templateTeamId: template.templateTeamId,
      version: template.version,
    };
  }

  @action
  private applyAddedTemplate(template?: AgencyAddOfficerTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      extras: template.extras,
      version: template.version,
      updatedAt: template.updatedAt,
    };
  }

  @action
  private applyUpdatedRosterTemplate(template?: AgencyUpdateOfficerTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      minOfficers: template.minOfficers,
      maxOfficers: template.maxOfficers,
      officerEmailToTeamId: template.officerEmailToTeamId,
      version: template.version,
      updatedAt: template.updatedAt,
    };
  }

  @action
  private applyUpdatedDetailsTemplate(template?: AgencyUpdateDetailsTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      agencyName: template.agencyName,
      swornOfficersCount: template.swornOfficersCount,
      program: template.program,
      version: template.version,
      updatedAt: template.updatedAt,
    };
  }

  @action
  private markOfficerInvited(email: string, team?: AgencyInviteTeam) {
    if (!this.template || !team)
      return;

    this.template = {
      ...this.template,
      officerEmailToTeamId: {
        ...this.officerEmailToTeamId,
        [normalizeEmail(email)]: team.id,
      },
    };
    this.inviteAllStatus = `Invited ${email}`;
  }

  @action
  private applyInviteAllResult(result?: AgencyInviteAllResult) {
    const teamsCount = result?.teams.length ?? 0;
    this.inviteAllStatus = `${teamsCount} ${teamsCount === 1 ? 'library' : 'libraries'} invited`;

    for (const error of result?.errors ?? [])
      this.rowErrors.set(normalizeEmail(error.officerEmail), error.message);
  }
}

function parseRows(value: unknown): AgencyOfficerRow[] {
  const parsed = parseJson(value);
  if (!Array.isArray(parsed))
    return [];

  return parsed.filter(isOfficerRow);
}

function isOfficerRow(value: unknown): value is AgencyOfficerRow {
  if (!value || typeof value !== 'object')
    return false;

  const row = value as Record<string, unknown>;
  return typeof row.email === 'string';
}

function normalizeEmailMap(value: unknown): Record<string, string> {
  const parsed = parseJson(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return {};

  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>)
      .filter(([, teamId]) => typeof teamId === 'string')
      .map(([email, teamId]) => [normalizeEmail(email), teamId as string]));
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function toOfficerInput(row: AgencyDashboardOfficerRow): AgencyOfficerRow {
  return {
    firstName: row.firstName,
    lastName: row.lastName,
    title: row.title,
    email: row.email,
    supervisorTitle: row.supervisorTitle,
    supervisorName: row.supervisorName,
    supervisorEmail: row.supervisorEmail,
  };
}

async function postSampleFile(ticket: AgencyUploadTicket, file: File): Promise<string | null> {
  const formData = new FormData();
  const uploadFields = parseJson(ticket.uploadFields);

  if (Array.isArray(uploadFields)) {
    for (const field of uploadFields) {
      if (isUploadField(field))
        formData.append(field.key, field.value);
    }
  } else if (uploadFields && typeof uploadFields === 'object') {
    for (const [key, value] of Object.entries(uploadFields))
      formData.append(key, String(value));
  }

  formData.append('file', file);

  const response = await fetch(ticket.uploadUrl, {
    method: 'POST',
    body: formData,
  });

  return response.ok ? null : `Upload failed with status ${response.status}.`;
}

function isUploadNotFoundError(error: ApiError): boolean {
  const graphQlErrors = error.data.graphQLError?.errors ?? [];
  return error.message.includes('UPLOAD_NOT_FOUND') ||
    graphQlErrors.some((graphQlError) =>
      graphQlError.message.includes('UPLOAD_NOT_FOUND') ||
      graphQlError.errorType === 'UPLOAD_NOT_FOUND');
}

function isUploadField(value: unknown): value is { key: string; value: string } {
  return Boolean(
    value &&
    typeof value === 'object' &&
    typeof (value as { key?: unknown }).key === 'string' &&
    typeof (value as { value?: unknown }).value === 'string',
  );
}

function createLocalId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isOfficerComplete(row: AgencyOfficerRow): boolean {
  return Boolean(
    row.firstName.trim() &&
    row.lastName.trim() &&
    row.title.trim() &&
    row.email.trim() &&
    row.supervisorTitle.trim() &&
    row.supervisorName.trim() &&
    row.supervisorEmail.trim(),
  );
}
